'use client'

import { memo } from 'react'
import Link from 'next/link'
import {
  AlertTriangle,
  Building2,
  CloudOff,
  CreditCard,
  FileSearch,
  Landmark,
  Lock,
  Scale,
  ScrollText,
  ShieldCheck,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import { NinConnectPlatformSection } from '@/app/(marketing)/_components/nin-connect-platform-section'
import { NinxlBanksHero, NINX_BANKS_SALES_EMAIL } from './ninxl-banks-hero'
import {
  NinxlBanksControlsRail,
  NinxlBanksInstitutionBento,
  NinxlBanksRailDivider,
  NinxlBanksRegulatoryMatrix,
  NinxlBanksRiskMatrix,
  NinxlBanksSectionBackdrop,
  NinxlBanksSettlementLedger,
  NinxlBanksTransactionRail,
} from './ninxl-banks-graphics'
import { NinxlScrollReveal } from './ninxl-scroll-reveal'
import { Ninxl8BitDivider, Ninxl8BitFrame, Ninxl8BitSection } from './ninxl-8bit-ui'
import { NinxlEvervaultShell } from './ninxl-evervault-shell'

export { NINX_BANKS_SALES_EMAIL }

export const BANKING_RISKS = [
  {
    code: 'RSK-A',
    title: 'Third-Party Model Exposure',
    desc: 'Customer PANs, account data, and trade logic routed through foreign inference APIs.',
    severity: 'high' as const,
    icon: CloudOff,
  },
  {
    code: 'RSK-B',
    title: 'Core Integration Gaps',
    desc: 'AI tools sit outside the ledger: no agentic posting, reconciliation, or settlement flow.',
    severity: 'high' as const,
    icon: AlertTriangle,
  },
  {
    code: 'RSK-C',
    title: 'Audit Trail Deficits',
    desc: 'Regulators cannot reconstruct AI-assisted credit, fraud, or compliance decisions.',
    severity: 'medium' as const,
    icon: ScrollText,
  },
  {
    code: 'RSK-D',
    title: 'Fraud Latency from Cloud',
    desc: 'Round-trips to external models slow real-time screening on high-volume payment rails.',
    severity: 'medium' as const,
    icon: ShieldCheck,
  },
] as const

const TRANSACTION_NODES = [
  { label: 'Payment Event', detail: 'Card, wire, or UPI event enters the secure rail.' },
  { label: 'Fraud Gate', detail: 'Real-time screening on on-prem models: no external hop.' },
  { label: 'Core Ledger', detail: 'Agent posts to core banking with full context.' },
  { label: 'Audit Archive', detail: 'Immutable record for SOX, PCI, and regulator review.' },
] as const

export const SETTLEMENT_ENTRIES = [
  { phase: 'Auth Hold', detail: 'Reserve funds inside PCI boundary.', side: 'debit' as const },
  { phase: 'Fraud Scan', detail: 'Score transaction against local risk models.', side: 'debit' as const },
  { phase: 'Core Post', detail: 'Settle to general ledger on sovereign infra.', side: 'credit' as const },
  { phase: 'Reconciliation', detail: 'Match against nostro and suspense accounts.', side: 'credit' as const },
  { phase: 'Audit Seal', detail: 'Cryptographic log for examiner retrieval.', side: 'credit' as const },
] as const

export const REGULATORY_MATRIX_ROWS = [
  { framework: 'PCI-DSS', requirement: 'Cardholder data never leaves CDE', cloud: false, legacy: true, ninx: true },
  { framework: 'SOX', requirement: 'Immutable AI decision audit trail', cloud: false, legacy: false, ninx: true },
  { framework: 'Basel III', requirement: 'Risk model governance on-prem', cloud: false, legacy: true, ninx: true },
  { framework: 'GDPR / DPDP', requirement: 'Customer PII localization', cloud: false, legacy: true, ninx: true },
  { framework: 'RBI / OCC', requirement: 'Supervisory access to AI logs', cloud: false, legacy: false, ninx: true },
  {
    framework: 'ZDR',
    requirement: 'On-device stays local; cloud credits use zero data retention',
    cloud: false,
    legacy: false,
    ninx: true,
  },
] as const

const CONTROL_CARDS = [
  { code: 'CTR-01', title: 'Segregation of Duties', desc: 'Maker-checker flows on every agentic posting.', icon: Scale },
  { code: 'CTR-02', title: 'Fraud Gate Policies', desc: 'Threshold rules enforced before core submission.', icon: ShieldCheck },
  { code: 'CTR-03', title: 'PCI Scope Isolation', desc: 'CDE workloads run on dedicated on-prem nodes.', icon: Lock },
  {
    code: 'CTR-04',
    title: 'ZDR on Cloud Credits',
    desc: 'On-device inference stays on bank systems. Optional cloud credits run under Zero Data Retention.',
    icon: CloudOff,
  },
  { code: 'CTR-05', title: 'Examiner Export', desc: 'One-click audit bundles for regulatory review.', icon: FileSearch },
  { code: 'CTR-06', title: 'Model Risk Mgmt', desc: 'Versioned models with drift monitoring.', icon: TrendingUp },
] as const

const INSTITUTIONS = [
  {
    code: 'RET-01',
    title: 'Retail Banking',
    desc: 'Branch operations, KYC, lending workflows, and customer service: all inside your perimeter.',
    icon: Landmark,
    featured: true,
  },
  {
    code: 'INV-02',
    title: 'Investment Banking',
    desc: 'Deal rooms, research ingestion, and compliance drafting without cloud data egress.',
    icon: TrendingUp,
  },
  {
    code: 'INS-03',
    title: 'Insurance',
    desc: 'Claims processing and underwriting on sovereign infrastructure.',
    icon: ShieldCheck,
  },
  {
    code: 'PAY-04',
    title: 'Payments & Fintech',
    desc: 'High-volume rails with sub-second fraud gates on local inference.',
    icon: CreditCard,
  },
] as const

const PARTNERSHIP_ITEMS = [
  'Bank-grade licensing with institution-level entitlements',
  'Core banking, payments, and CRM integration services',
  'Dedicated financial services security and compliance team',
] as const

export const NinxlBanksSection = memo(function NinxlBanksSection() {
  return (
    <>
      <NinxlBanksHero />

      <NinxlBanksRailDivider label="RISK MATRIX" />

      <Ninxl8BitSection data-section="risk" className="relative overflow-hidden">
        <NinxlBanksSectionBackdrop variant="risk" />
        <div className="mx-auto max-w-5xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-left">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">THE CHALLENGE</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                Banking risk matrix
              </h2>
              <p className="ninxl-8bit-body max-w-2xl">
                Four regulated risks mapped by severity: where cloud-first AI breaks compliance and core connectivity.
              </p>
            </div>
          </NinxlScrollReveal>
          <NinxlBanksRiskMatrix risks={BANKING_RISKS} />
        </div>
      </Ninxl8BitSection>

      <NinxlBanksRailDivider label="TXN RAIL" />

      <Ninxl8BitSection alt data-section="rail" className="relative overflow-hidden">
        <NinxlBanksSectionBackdrop variant="rail" />
        <div className="mx-auto max-w-6xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-left">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">SECURE RAIL</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                Transaction processing rail
              </h2>
              <p className="ninxl-8bit-body max-w-2xl">
                From payment event to audit archive: every node runs inside your banking perimeter.
              </p>
            </div>
          </NinxlScrollReveal>
          <Ninxl8BitFrame label="SETTLEMENT RAIL · LIVE">
            <NinxlBanksTransactionRail nodes={TRANSACTION_NODES} />
          </Ninxl8BitFrame>
        </div>
      </Ninxl8BitSection>

      <NinxlBanksRailDivider label="LEDGER" />

      <Ninxl8BitSection data-section="ledger" className="relative overflow-hidden">
        <NinxlBanksSectionBackdrop variant="ledger" />
        <div className="mx-auto max-w-4xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-left">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">SETTLEMENT</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                Double-entry settlement ledger
              </h2>
              <p className="ninxl-8bit-body max-w-2xl">
                A vertical ledger view of agentic settlement: debit and credit phases with examiner-ready seals.
              </p>
            </div>
          </NinxlScrollReveal>
          <NinxlBanksSettlementLedger entries={SETTLEMENT_ENTRIES} />
        </div>
      </Ninxl8BitSection>

      <NinxlBanksRailDivider label="REGULATORY" />

      <Ninxl8BitSection alt data-section="regulatory">
        <div className="mx-auto max-w-5xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-center">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">COMPLIANCE FIT</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                Regulatory fit matrix
              </h2>
              <p className="ninxl-8bit-body mx-auto max-w-2xl">
                Framework-by-framework assessment: cloud SaaS, legacy on-prem, and NINX Banks side by side.
              </p>
            </div>
          </NinxlScrollReveal>
          <NinxlScrollReveal delay={0.08}>
            <NinxlBanksRegulatoryMatrix rows={REGULATORY_MATRIX_ROWS} />
          </NinxlScrollReveal>
        </div>
      </Ninxl8BitSection>

      <NinxlBanksRailDivider label="CONTROLS" />

      <Ninxl8BitSection data-section="controls" className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-left">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">GOVERNANCE</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                Controls rail
              </h2>
              <p className="ninxl-8bit-body max-w-2xl">
                Scroll the control cards: numbered CTR codes for audit, fraud, PCI, and model risk.
              </p>
            </div>
          </NinxlScrollReveal>
          <NinxlBanksControlsRail controls={CONTROL_CARDS} />
        </div>
      </Ninxl8BitSection>

      <NinxlBanksRailDivider label="INSTITUTIONS" />

      <Ninxl8BitSection alt data-section="institutions" className="relative overflow-hidden">
        <NinxlBanksSectionBackdrop variant="institutions" />
        <div className="mx-auto max-w-5xl px-6">
          <NinxlScrollReveal>
            <div className="mb-8 text-left">
              <p className="ninxl-8bit-label ninxl-theme-text-accent mb-2 tracking-[0.2em]">WHO WE SERVE</p>
              <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-lg md:text-xl">
                Institution profiles
              </h2>
              <p className="ninxl-8bit-body max-w-2xl">
                Bento layout by institution type: retail spans full height, specialty banks tile alongside.
              </p>
            </div>
          </NinxlScrollReveal>
          <NinxlBanksInstitutionBento institutions={INSTITUTIONS} />
        </div>
      </Ninxl8BitSection>

      <NinxlBanksRailDivider label="CONNECT" />

      <Ninxl8BitSection id="connect-platform" data-section="connect-platform" className="scroll-mt-24">
        <div className="mx-auto max-w-6xl px-6">
          <NinConnectPlatformSection variant="banks" />
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <NinxlBanksRailDivider label="PARTNER" />

      <Ninxl8BitSection className="py-20 md:py-24" data-section="contact">
        <NinxlScrollReveal variant="scale">
          <div className="mx-auto max-w-3xl px-6 pt-4">
            <div className="ninxl-banks-partnership-shell ninxl-pixel-border relative">
              <div className="ninxl-banks-partnership-tab ninxl-8bit-label">PARTNERSHIP · FORM-BNK-07</div>
              <NinxlEvervaultShell
                className="ninxl-banks-partnership-cipher"
                contentClassName="p-6 md:p-8 pt-8 text-left"
              >
                <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-base md:text-lg">
                  Partner with NINX Banks
                </h2>
                <p className="ninxl-8bit-body mb-6">
                  Schedule a regulated AI assessment with our financial services team: core integration, PCI scope, and pilot design included.
                </p>

                <ul className="mb-8 space-y-2">
                  {PARTNERSHIP_ITEMS.map((item) => (
                    <li key={item} className="ninxl-banks-partnership-check flex items-start gap-3">
                      <Wallet className="ninxl-theme-text-accent mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                      <span className="ninxl-theme-text-muted text-sm">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                  <Link
                    href={`mailto:${NINX_BANKS_SALES_EMAIL}?subject=NINX%20Banks%20Partnership%20Inquiry`}
                    className="ninxl-pixel-btn ninxl-pixel-btn-primary pointer-events-auto"
                  >
                    Talk to Banking Team
                  </Link>
                  <div className="ninxl-8bit-label flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5" aria-hidden />
                    Pilot programs for regulated financial institutions
                  </div>
                </div>
              </NinxlEvervaultShell>
            </div>
          </div>
        </NinxlScrollReveal>
      </Ninxl8BitSection>
    </>
  )
})